import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import PureImage from './PureImage';
import { ImageScale } from './ImageScale';
import { useConfig } from '../../../lib/hooks/config';
import useFeature from '../../../lib/hooks/useFeature';
import { Api } from '../../../lib/types/apiSpec';
import { FeatureToggleCodename } from '../../../lib/types/enums';
import { isCourseType, isTrainingType } from '../../../lib/utils/trainings/typesChecking';
import EventIcon from '../../../lib/icons/EventIcon';
import styles from './PureImage.module.scss';
import { useApplication } from '../../../lib/context/ApplicationConfigurationContext';
import { classes } from '../../../lib/utils/cssUtils';

export type LearningImageProps = {
  id?: number | string;
  name: string;
  type: Api.LearningType;
  className?: string;
  imageClassName?: string;
  imageURL?: string;
  scale?: Partial<ImageScale>;
  fallbackSrc?: string;
} & Partial<Pick<Api.EventLibrary, 'eventStartDate'>>;

export const LearningImage = ({
  id,
  type,
  name,
  className,
  imageClassName,
  scale,
  fallbackSrc,
  imageURL,
  eventStartDate,
}: LearningImageProps) => {
  const config = useConfig();
  const { locale } = useApplication();
  const isLitmosImagesFeature = useFeature(FeatureToggleCodename.imagesFromLitmos);

  const imagePath = (() => {
    if (isTrainingType(type) && !isLitmosImagesFeature) {
      return `${isCourseType(type) ? config?.courseImagesRootPath : config?.learningPathImagesRootPath}/${id}.jpg`;
    }

    return imageURL;
  })();

  const fallbackImage = {
    [Api.LearningType.Course]: config?.coursesFallbackImage,
    [Api.LearningType.LearningPath]: config?.learningPathFallbackImage,
    [Api.LearningType.Microlearning]: config?.microlearningFallbackImage,
    [Api.LearningType.Document]: config?.documentFallbackImage,
    [Api.LearningType.Video]: config?.videoFallbackImage,
    [Api.LearningType.Event]: undefined,
  };

  const fallbackImageSrc = fallbackSrc ?? fallbackImage[type];

  if (type === Api.LearningType.Event) {
    const startDate = dayjs(eventStartDate).locale(locale);
    const startMonth = startDate.format('MMM').toUpperCase();
    const startDay = startDate.format('D');

    return (
      <div className={classes(styles.eventIconWrapper, imageClassName)}>
        <EventIcon month={startMonth} day={startDay} className={styles.eventIcon} />
      </div>
    );
  }

  return (
    <PureImage
      src={imagePath || fallbackImageSrc || ''}
      alt={name}
      className={className}
      imageClassName={imageClassName}
      fallbackSrc={fallbackImage[type]}
      scale={scale}
    />
  );
};
